import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isInflightOrTrueModifySelector } from 'selectors/reservationSelectors';
import { vehicleUpgradeSelector } from 'selectors/vehicleUpgradeSelectors';
import { isFetchingEndpointSelector } from 'selectors/fetchingSelectors';
import useActions from 'hooks/useActions';
import { getInPathVehicleUpgradeDetails } from "actions/reservation/getInPathVehicleUpgradeDetails";

/**
 * useVehicleUpgradeDetails checks the session for an upgrade available flag and checks whether the upgrade
 * details are already in session. If they are not, it makes a request to the upgradeRates service. This hook
 * returns the flag and the details (or null)
 *
 * @param {object} options
 * @param {bool}   options.shouldGetDetailsConditional - allows components extra control over whether to make the service call
 *                 to retrieve details. Helps prevent multiple calls to the upgradeRates service when multiple
 *                 IPVU banners are on one page. (InPathVehicleUpgradeRentalDetails and InPathVehicleUpgradeReview)
 */
export default function useVehicleUpgradeDetails({ shouldGetDetailsConditional = true } = {}) {
  const { inPathUpgradeAvailable, inPathUpgradeDetails } = useSelector(vehicleUpgradeSelector);
  const isModifyingReservation = useSelector(isInflightOrTrueModifySelector);
  const isFechingRates = useSelector(state =>
    isFetchingEndpointSelector(state, { endpoint: 'reservations/upgrade/rates' })
  );

  const getDetailsHandler = useActions(getInPathVehicleUpgradeDetails);

  const shouldGetDetails =
    isModifyingReservation && inPathUpgradeAvailable && !inPathUpgradeDetails && shouldGetDetailsConditional;

  useEffect(
    () => {
      if (shouldGetDetails) {
        getDetailsHandler();
      }
    },
    [shouldGetDetails]
  );

  return { inPathUpgradeAvailable, inPathUpgradeDetails, isFechingRates };
}
